import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useLiveViewStateStore } from '../LiveViewStore'
import { Blackmask, COLORS, NormalizedPoint } from '../Blackmask'
import { useBlackmaskApi } from './useBlackmaskAPI'
import { useSupportedFunctionalities } from './useSupportedFunctionalities'

export const useBlackmaskOperations = (
    canvasRef: React.RefObject<HTMLCanvasElement>,
    imageRef: React.RefObject<HTMLIFrameElement>
) => {
    const { baseURL, blackmaskEnabled, setBlackmaskEnabled } = useLiveViewStateStore()
    const [draggedPointInfo, setDraggedPointInfo] = useState<{
        maskIndex: number
        pointIndex: number
    } | null>(null)
    const [editMode, setEditMode] = useState<number | null>(null)
    const [hoveredPoint, setHoveredPoint] = useState<{
        maskIndex: number
        pointIndex: number
    } | null>(null)
    const [stickToEdges, setStickToEdges] = useState(true)
    const stickThreshold = 5
    const [colorIndices, setColorIndices] = useState<number[]>([0])
    const { maxPoints, MAX_OVERLAYS, IS_BLACK_MASK_SUPPORTED, IS_POLYGON_SUPPORTED } = useSupportedFunctionalities()

    const {
        blackmasks,
        setBlackmasks,
        fetchBlackmasks,
        addNewBlackmask,
        editBlackmask,
        deleteBlackmaskById,
        getBlackmaskEnabled,
        setBlackmaskEnabled: setBlackmaskEnabledApi
    } = useBlackmaskApi(baseURL, imageRef)

    const debouncedEditBlackmask = useRef(
        debounce((id: number, points: { x: number; y: number }[]) => {
            if (canvasRef.current) {
                editBlackmask(id, points, canvasRef.current)
            }
        }, 500)
    ).current

    const toCanvasCoord = (p: NormalizedPoint, canvas: HTMLCanvasElement) => ({
        x: p.x * canvas.width,
        y: p.y * canvas.height
    })

    const toNormalizedCoord = (x: number, y: number, canvas: HTMLCanvasElement): NormalizedPoint => ({
        x: x / canvas.width,
        y: y / canvas.height
    })

    useEffect(() => {
        getBlackmaskEnabled().then(setBlackmaskEnabled)
    }, [])

    useEffect(() => {
        fetchBlackmasks().then((masks: Blackmask[]) => {
            if (masks) {
                setColorIndices(masks.map((_, index) => index % COLORS.length))
            }
        })
    }, [baseURL])

    const handleBlackmaskEnabledChangeAction = (enabled: boolean) => {
        const previousEnabled = blackmaskEnabled
        setBlackmaskEnabled(enabled)
        setBlackmaskEnabledApi(enabled).catch(() => {
            setBlackmaskEnabled(previousEnabled)
            alert('Could not change the status of blackmask')
        })
    }

    const updateCanvasWrapper = (imageRefFromChild?: HTMLImageElement) => {
        const canvas = canvasRef.current
        const image = imageRefFromChild || imageRef.current
        if (canvas && image) {
            const updateCanvasSize = () => {
                const { width, height } = image.getBoundingClientRect()
                canvas.width = width
                canvas.height = height
                drawPointsAndLines()
            }

            image.onload = updateCanvasSize
            image.addEventListener('load', updateCanvasSize)
            updateCanvasSize()
            window.addEventListener('resize', updateCanvasSize)

            return () => {
                window.removeEventListener('resize', updateCanvasSize)
                image.removeEventListener('load', updateCanvasSize)
            }
        }
    }

    useEffect(() => {
        updateCanvasWrapper()
    }, [blackmasks, hoveredPoint])

    const drawPointsAndLines = () => {
        const canvas = canvasRef.current
        const ctx = canvas?.getContext('2d')
        if (!ctx || !canvas) {
            return
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        blackmasks.forEach((mask, maskIndex) => {
            const color = COLORS[colorIndices[maskIndex]]
            const points = mask.points.map((p) => toCanvasCoord(p, canvas))
            if (points.length === maxPoints) {
                ctx.beginPath()
                ctx.moveTo(points[0].x, points[0].y)
                for (let i = 1; i < points.length; i++) {
                    ctx.lineTo(points[i].x, points[i].y)
                }
                ctx.closePath()
                ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
                ctx.fill()
            }

            if (points.length > 0) {
                ctx.beginPath()
                ctx.moveTo(points[0].x, points[0].y)
                for (let i = 1; i < points.length; i++) {
                    ctx.lineTo(points[i].x, points[i].y)
                }
                if (points.length === maxPoints) {
                    ctx.lineTo(points[0].x, points[0].y)
                }
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.stroke()
            }

            points.forEach((point, pointIndex) => {
                ctx.beginPath()
                ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI)
                ctx.fillStyle =
                    hoveredPoint && hoveredPoint.maskIndex === maskIndex && hoveredPoint.pointIndex === pointIndex
                        ? 'white'
                        : color
                ctx.fill()
            })
        })
    }

    const stickCoordinate = (value: number, canvasSize: number): number => {
        const threshold = stickThreshold / canvasSize
        if (value <= threshold) return 0
        if (value >= 1 - threshold) return 1
        return value
    }

    const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
        if (editMode === null) return
        const canvas = canvasRef.current
        if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top
            const norm = toNormalizedCoord(x, y, canvas)

            if (stickToEdges) {
                norm.x = stickCoordinate(norm.x, canvas.width)
                norm.y = stickCoordinate(norm.y, canvas.height)
            }

            const newBlackmasks = [...blackmasks]
            const activeMask = newBlackmasks[editMode]
            if (!activeMask) return
            if (activeMask.points.length < maxPoints) {
                activeMask.points.push(norm)
                setBlackmasks(newBlackmasks)

                if (activeMask.points.length === maxPoints) {
                    const pxPoints = activeMask.points.map((p) => toCanvasCoord(p, canvas))
                    addNewBlackmask(pxPoints, canvas)
                    setEditMode(editMode)
                }
            }
        }
    }

    const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
        if (editMode === null) return
        const canvas = canvasRef.current
        if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top

            const activeMask = blackmasks[editMode]
            if (!activeMask) return
            const pointIndex = activeMask.points.findIndex((p) => {
                const { x: px, y: py } = toCanvasCoord(p, canvas)
                return Math.sqrt((px - x) ** 2 + (py - y) ** 2) < 10
            })

            if (pointIndex !== -1) {
                setDraggedPointInfo({ maskIndex: editMode, pointIndex })
            }
        }
    }

    const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current
        if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top

            const norm = toNormalizedCoord(x, y, canvas)
            if (stickToEdges) {
                norm.x = stickCoordinate(norm.x, canvas.width)
                norm.y = stickCoordinate(norm.y, canvas.height)
            }

            if (draggedPointInfo !== null) {
                const newBlackmasks = [...blackmasks]
                const draggedMask = newBlackmasks[draggedPointInfo.maskIndex]
                if (!draggedMask) return
                draggedMask.points[draggedPointInfo.pointIndex] = norm
                setBlackmasks(newBlackmasks)

                if (draggedMask.points.length === maxPoints) {
                    const pxPoints = draggedMask.points.map((p) => toCanvasCoord(p, canvas))
                    debouncedEditBlackmask(draggedMask.id, pxPoints)
                }
            } else {
                let foundHoveredPoint = false
                blackmasks.forEach((mask, maskIndex) => {
                    mask.points.forEach((p, pointIndex) => {
                        const pt = toCanvasCoord(p, canvas)
                        if (Math.sqrt((pt.x - x) ** 2 + (pt.y - y) ** 2) < 10) {
                            setHoveredPoint({ maskIndex, pointIndex })
                            foundHoveredPoint = true
                            canvas.style.cursor = 'pointer'
                        }
                    })
                })
                if (!foundHoveredPoint) {
                    setHoveredPoint(null)
                    canvas.style.cursor = 'default'
                }
            }
        }
    }

    const handleMouseUp = () => setDraggedPointInfo(null)

    const handleEdit = (index: number) => setEditMode(index)

    const handleDelete = async (index: number) => {
        if (blackmasks[index].points.length === maxPoints) {
            try {
                await deleteBlackmaskById(blackmasks[index].id)
                setEditMode(blackmasks.length > 1 ? 0 : null)
            } catch (err) {
                console.error('Failed to delete blackmask:', err)
            }
        }
    }

    const handleAddShape = () => {
        if (blackmasks.length >= MAX_OVERLAYS) return
        if (blackmasks.length > 0 && blackmasks[blackmasks.length - 1].points.length !== maxPoints) return

        const newColorIndices = [...colorIndices]
        let newColorIndex = 0
        while (newColorIndices.includes(newColorIndex)) {
            newColorIndex = (newColorIndex + 1) % COLORS.length
        }
        newColorIndices.push(newColorIndex)
        setColorIndices(newColorIndices)
        setBlackmasks([...blackmasks, { id: blackmasks.length, points: [] }])
        setEditMode(blackmasks.length)
    }

    return {
        blackmasks,
        editMode,
        stickToEdges,
        colorIndices,
        IS_BLACK_MASK_SUPPORTED,
        IS_POLYGON_SUPPORTED,
        maxPoints,
        MAX_OVERLAYS,
        updateCanvasWrapper,
        handleCanvasClick,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleEdit,
        handleDelete,
        handleAddShape,
        setStickToEdges,
        blackmaskEnabled,
        handleBlackmaskEnabledChangeAction
    }
}
